import { Box, Typography, LinearProgress } from '@mui/material';
import { ThemePalette } from 'mui.theme';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

// Function to calculate the position of the arrow
const getArrowPosition = (value: number) => {
  let valueToUse = value;
  if (value === 100) {
    valueToUse = 99.8;
  }
  const position = (valueToUse / 100) * 100; // Calculate percentage
  return `calc(${position}% - 8px)`; // Adjust the position for the arrow
};

export const Guage = ({
  value,
  enableDarkTheme,
  maxValue,
  minValue,
  unitSystem,
  metricType,
  startRange,
  endRange,
}: {
  value: number;
  enableDarkTheme: boolean;
  maxValue: number;
  minValue: number;
  startRange: number;
  endRange: number;
  unitSystem: string;
  metricType: string;
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  function calculatePercentage(
    currentValue: number,
    minValue: number,
    maxValue: number
  ) {
    if (currentValue < minValue) {
      return 0;
    }
    if (currentValue > maxValue) {
      return 100;
    }
    return ((currentValue - minValue) / (maxValue - minValue)) * 100;
  }

  // Calculate green zone percentages based on the FULL range (startRange to endRange)
  const greenStart = ((minValue - startRange) / (endRange - startRange)) * 100;
  const greenEnd = ((maxValue - startRange) / (endRange - startRange)) * 100;

  const linearGradientImperialTemp = `linear-gradient(
    to right,
    ${ThemePalette.typography.danger} 0%, 
    ${ThemePalette.typography.warning} ${greenStart - 7}%, 
    ${ThemePalette.typography.success} ${greenStart - 5}%, 
    ${ThemePalette.typography.success} ${greenStart}%, 
    ${ThemePalette.typography.success} ${greenStart + 5}%, 
    ${ThemePalette.typography.success} ${greenEnd - 5}%, 
    ${ThemePalette.typography.success} ${greenEnd}%, 
    ${ThemePalette.typography.success} ${greenEnd + 5}%, 
    ${ThemePalette.typography.warning} ${greenEnd + 7}%, 
    ${ThemePalette.typography.danger} 100%
  )`;

  // Default gradient
  const defaultGradient = `linear-gradient(
    to right,
    ${ThemePalette.typography.danger} 0%, 
    ${ThemePalette.typography.danger} ${greenStart - 5}%, 
    ${ThemePalette.typography.warning} ${greenStart}%, 
    ${ThemePalette.typography.success} ${greenStart + 5}%, 
    ${ThemePalette.typography.success} ${greenEnd - 5}%, 
    ${ThemePalette.typography.warning} ${greenEnd}%, 
    ${ThemePalette.typography.danger} ${greenEnd + 5}%, 
    ${ThemePalette.typography.danger} 100%
  )`;

  const percentageValue = calculatePercentage(value, startRange, endRange);

  return (
    <Box position="relative" width="100%">
      {/* Arrow */}
      <Typography
        variant="body2"
        style={{
          position: 'absolute',
          top: -10,
          zIndex: 1,
          left: getArrowPosition(percentageValue),
          transform: 'rotate(180deg)', // Rotate the arrow to point downwards
          color: palette.textPrimary,
          opacity: 1,
        }}
      >
        ▲
      </Typography>

      {/* Gradient Progress Bar */}
      <LinearProgress
        variant="determinate"
        value={100} // Set to 100 since the gradient spans the full width
        sx={{
          height: 10,
          borderRadius: 5,
          background: 'lightgray',
          '& .MuiLinearProgress-bar': {
            borderRadius: 5,
            background:
              metricType === 'Temperature' && unitSystem === 'imperial'
                ? linearGradientImperialTemp
                : defaultGradient,
          },
        }}
      />

      {/* Labels */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 1,
        }}
      >
        <Typography
          sx={{
            color: enableDarkTheme
              ? ThemePalette.typography.wheat
              : ThemePalette.typography.black,
            fontSize: '12px',
          }}
        >
          {startRange}
        </Typography>
        <Typography
          sx={{
            color: enableDarkTheme
              ? ThemePalette.typography.wheat
              : ThemePalette.typography.black,
            fontSize: '12px',
          }}
        >
          {endRange}
        </Typography>
      </Box>
    </Box>
  );
};
